import React from 'react'
import { motion } from 'framer-motion'
import { ExternalLink, Github, Star, Code } from 'lucide-react'
import { Project } from '../types'
import { fadeUp, staggerContainer, viewportOnce, cardHover } from '../lib/motion'

interface ProjectsProps {
  projects: Project[]
}

const Projects: React.FC<ProjectsProps> = ({ projects }) => {
  const featuredProjects = projects.filter(project => project.featured)
  const otherProjects = projects.filter(project => !project.featured)

  const ProjectCard: React.FC<{ project: Project; featured?: boolean }> = ({ project, featured = false }) => (
    <motion.div
      className={`card p-4 sm:p-6 h-full transition-shadow duration-300 group ${
        featured ? 'ring-2 ring-primary-200 dark:ring-primary-800' : ''
      }`}
      variants={fadeUp}
      whileHover={cardHover.whileHover}
      whileTap={cardHover.whileTap}
      transition={cardHover.transition}
    >
      {featured && (
        <div className="flex items-center mb-4">
          <Star className="h-4 w-4 text-yellow-500 mr-2" />
          <span className="text-sm font-medium text-yellow-600 dark:text-yellow-400">Featured Project</span>
        </div>
      )}

      <div className="flex items-start justify-between mb-3 sm:mb-4">
        <h3 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors pr-2">
          {project.name}
        </h3>
        <div className="flex space-x-1 sm:space-x-2 flex-shrink-0">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="View source code"
            >
              <Github className="h-4 w-4 sm:h-5 sm:w-5" />
            </a>
          )}
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="p-1.5 sm:p-2 text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 transition-colors"
              aria-label="View live project"
            >
              <ExternalLink className="h-4 w-4 sm:h-5 sm:w-5" />
            </a>
          )}
        </div>
      </div>

      <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 leading-relaxed">
        {project.description}
      </p>

      <div className="flex flex-wrap gap-1.5 sm:gap-2 mb-4 sm:mb-6">
        {project.technologies.map((tech, index) => (
          <span
            key={index}
            className="px-2 sm:px-3 py-1 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 rounded-full text-xs sm:text-sm font-medium"
          >
            {tech}
          </span>
        ))}
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between pt-3 sm:pt-4 border-t border-gray-200 dark:border-gray-700 space-y-2 sm:space-y-0">
        <div className="flex items-center text-xs sm:text-sm text-gray-500 dark:text-gray-400">
          <Code className="h-3 w-3 sm:h-4 sm:w-4 mr-2" />
          {project.technologies.length} technologies
        </div>
        <div className="flex space-x-2 sm:space-x-3">
          {project.github && (
            <a
              href={project.github}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs sm:text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              Source
            </a>
          )}
          {project.url && (
            <a
              href={project.url}
              target="_blank"
              rel="noopener noreferrer"
              className="text-xs sm:text-sm font-medium text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 transition-colors"
            >
              Live Demo
            </a>
          )}
        </div>
      </div>
    </motion.div>
  )

  return (
    <section id="projects" className="py-20 bg-white dark:bg-gray-900">
      <div className="container-max section-padding">
        <motion.div
          className="text-center mb-12 sm:mb-16"
          initial="hidden"
          whileInView="visible"
          viewport={viewportOnce}
          variants={fadeUp}
        >
          <h2 className="text-2xl sm:text-3xl md:text-4xl font-bold text-gray-900 dark:text-white mb-4">
            Featured Projects
          </h2>
          <div className="w-24 h-1 bg-gradient-to-r from-primary-500 to-accent-500 mx-auto rounded-full"></div>
          <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mt-4 max-w-2xl mx-auto px-4">
            A showcase of my recent work in backend development and system architecture
          </p>
        </motion.div>

        {/* Featured Projects */}
        {featuredProjects.length > 0 && (
          <div className="mb-12 sm:mb-16">
            <motion.div
              className="grid sm:grid-cols-1 md:grid-cols-2 gap-6 sm:gap-8"
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={staggerContainer}
            >
              {featuredProjects.map((project, index) => (
                <ProjectCard key={index} project={project} featured={true} />
              ))}
            </motion.div>
          </div>
        )}

        {/* Other Projects */}
        {otherProjects.length > 0 && (
          <div>
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-6 sm:mb-8 text-center">
              Other Projects
            </h3>
            <motion.div
              className="grid sm:grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 sm:gap-6"
              initial="hidden"
              whileInView="visible"
              viewport={viewportOnce}
              variants={staggerContainer}
            >
              {otherProjects.map((project, index) => (
                <ProjectCard key={index} project={project} />
              ))}
            </motion.div>
          </div>
        )}

        {/* Project Stats */}
        <div className="mt-12 sm:mt-16 grid grid-cols-1 sm:grid-cols-3 gap-6 sm:gap-8">
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {projects.length}
            </div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Total Projects</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {featuredProjects.length}
            </div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Featured Projects</div>
          </div>
          <div className="text-center">
            <div className="text-2xl sm:text-3xl font-bold text-primary-600 dark:text-primary-400 mb-2">
              {new Set(projects.flatMap(p => p.technologies)).size}
            </div>
            <div className="text-sm sm:text-base text-gray-600 dark:text-gray-400">Technologies Used</div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="mt-12 sm:mt-16 text-center">
          <div className="bg-gradient-to-r from-primary-50 to-blue-50 dark:from-primary-900/20 dark:to-blue-900/20 rounded-2xl p-6 sm:p-8">
            <h3 className="text-xl sm:text-2xl font-bold text-gray-900 dark:text-white mb-3 sm:mb-4">
              Interested in collaborating?
            </h3>
            <p className="text-sm sm:text-base text-gray-600 dark:text-gray-400 mb-4 sm:mb-6 max-w-2xl mx-auto px-4">
              I'm always open to discussing new opportunities and interesting projects.
              Let's build something amazing together!
            </p>
            <a
              href="#contact"
              className="btn-primary px-6 sm:px-8 py-2 sm:py-3 text-base sm:text-lg font-semibold rounded-full hover:scale-105 transition-transform duration-200 inline-block"
            >
              Get In Touch
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}

export default Projects
